import classNames from "classnames";
import { type CMSStandaloneImageDTO } from "~/features/cms/types/dto";
import { resolveUrl } from "~/utils/url/urlResolver";
import { CMSMedia } from "~/features/cms/components/cmsMedia/CMSMedia";
import { styled } from "~/common/mui";
import type { Theme } from "~/common/mui";

export interface CMSStandaloneImageProps extends Omit<CMSStandaloneImageDTO, "type"> {
  className?: string;
}

const JUSTIFY_MAP: Record<string, string> = {
  LEFT: "flex-start",
  CENTER: "center",
  RIGHT: "flex-end",
};

const widthStyles: Record<string, (theme: Theme) => Record<string, unknown>> = {
  S100: () => ({ width: "100%" }),
  S75: (theme) => ({
    width: "100%",
    [theme.breakpoints.up("desktop-s")]: { width: "75%" },
  }),
  S50: (theme) => ({
    width: "100%",
    [theme.breakpoints.up("desktop-s")]: { width: "50%" },
  }),
  S25: (theme) => ({
    width: "100%",
    [theme.breakpoints.up("desktop-s")]: { width: "25%" },
  }),
};

export const CMSStandaloneImage = (props: CMSStandaloneImageProps) => {
  if (!props.media) {
    return null;
  }
  const { url, target, caption, className, alignment = "CENTER", size = "S100" } = props;

  let media = <CMSMedia {...props.media} className={props.className} />;
  if (url) {
    media = (
      <a href={resolveUrl(url)} target={target}>
        {media}
      </a>
    );
  }
  return (
    <Root className={classNames("scha__cms", className)} sx={{ justifyContent: JUSTIFY_MAP[alignment] }}>
      <Figure width={size}>
        {media}
        {!!caption && <Caption>{caption}</Caption>}
      </Figure>
    </Root>
  );
};

const Root = styled("div")({ display: "flex" });

const Figure = styled("figure", { shouldForwardProp: (prop) => prop !== "width" })<{ width: string }>(
  ({ theme, width }) => ({
    margin: 0,
    ...(widthStyles[width]?.(theme) ?? { width: "100%" }),
  }),
);

const Caption = styled("figcaption")(({ theme }) => ({
  display: "block",
  alignSelf: "flex-start",
  paddingBottom: theme.spacing(1),
  paddingTop: theme.spacing(1),
}));
